import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Spin, Tag, Typography, Divider, Space, message, Tooltip, Alert } from 'antd';
import {
    QrcodeOutlined,
    DownloadOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getQrMeta, downloadQrBlob, rotateQr } from '../../../config/services/patrolService';
import { parseApiError } from '../../../config/services/errorHandler';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

/**
 * QrPanel — Drawer panel to manage a checkpoint's QR code.
 * Fetches QR metadata, previews PNG via authenticated blob, supports PDF/PNG download,
 * and QR rotation with destructive confirmation.
 *
 * Props:
 *   checkpointId    {number}
 *   checkpointName  {string}
 *   checkpoint      {object}  optional — full checkpoint record (location, unit, status,
 *                              scanRadiusMeters, allowOpenFollowUp, isStartingPoint,
 *                              description, address, incidentType…). When provided, a
 *                              "Checkpoint Details" section is shown above the QR info.
 */
const QrPanel = ({ checkpointId, checkpointName, checkpoint, visible, onClose }) => {
    const queryClient = useQueryClient();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rotateConfirmVisible, setRotateConfirmVisible] = useState(false);
    // Set when the backend can't regenerate an older QR credential (409) —
    // the only fix is to rotate, so we show that action instead of a generic error.
    const [qrConflict, setQrConflict] = useState(null);

    // ── QR Metadata ──────────────────────────────────────────
    const {
        data: qrMeta,
        isLoading: metaLoading,
        isError: metaError,
        refetch: refetchMeta,
    } = useQuery(
        ['checkpoint-qr', checkpointId],
        () => getQrMeta(checkpointId),
        {
            enabled: !!checkpointId && visible,
            staleTime: 30_000,
        }
    );

    // ── Authenticated PNG Preview ────────────────────────────
    const fetchPreview = useCallback(async () => {
        if (!checkpointId) return;
        setPreviewLoading(true);
        // Revoke old URL first
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        try {
            const res = await downloadQrBlob(checkpointId, 'png');
            const objectUrl = URL.createObjectURL(res.data);
            setPreviewUrl(objectUrl);
        } catch (err) {
            if (err?.response?.status === 409) {
                setQrConflict(parseApiError(err).message);
            } else {
                message.error('Failed to load QR preview.');
            }
        } finally {
            setPreviewLoading(false);
        }
    }, [checkpointId]);

    useEffect(() => {
        if (visible && checkpointId && qrMeta) {
            setQrConflict(null);
            fetchPreview();
        }
        // Revoke on unmount / hide
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [visible, checkpointId, qrMeta?.version]);

    // ── Download ─────────────────────────────────────────────
    const handleDownload = async (format) => {
        try {
            const res = await downloadQrBlob(checkpointId, format);
            // Try Content-Disposition filename, fallback to generated name
            const disposition = res.headers?.['content-disposition'] ?? '';
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match ? match[1].replace(/['"]/g, '') : `qr-${checkpointName}-v${qrMeta?.version}.${format}`;
            const a = document.createElement('a');
            a.href = URL.createObjectURL(res.data);
            a.download = filename;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            if (err?.response?.status === 409) {
                setQrConflict(parseApiError(err).message);
            } else {
                message.error(parseApiError(err).message);
            }
        }
    };

    // ── Rotate ────────────────────────────────────────────────
    const rotateMutation = useMutation(() => rotateQr(checkpointId), {
        onSuccess: (newMeta) => {
            message.success('QR rotated successfully. Please reprint and replace all older labels.');
            setQrConflict(null);
            queryClient.invalidateQueries(['checkpoint-qr', checkpointId]);
            setRotateConfirmVisible(false);
        },
        onError: (err) => {
            const parsed = parseApiError(err);
            message.error(parsed.message);
            setRotateConfirmVisible(false);
        },
    });

    const loading = metaLoading || previewLoading;

    return (
        <>
            <Modal
                title={
                    <Space>
                        <QrcodeOutlined style={{ fontSize: 18, color: '#6366f1' }} />
                        <span>QR Code — {checkpointName}</span>
                    </Space>
                }
                open={visible}
                onCancel={onClose}
                footer={null}
                width={480}
                destroyOnClose
            >
                {checkpoint && (
                    <div
                        style={{
                            background: '#fafafa',
                            borderRadius: 10,
                            padding: '14px 18px',
                            marginBottom: 16,
                            border: '1px solid #eee',
                        }}
                    >
                        <Text strong style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 8 }}>
                            Checkpoint Details
                        </Text>
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Space size={6}>
                                <Tag color={checkpoint.status === 'active' ? 'green' : 'red'}>
                                    {checkpoint.status?.toUpperCase() ?? '—'}
                                </Tag>
                                {checkpoint.isStartingPoint && <Tag color="gold">Starting Point</Tag>}
                            </Space>
                            {(checkpoint.location || checkpoint.unit) && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Location</Text>
                                    <Text style={{ fontSize: 12 }}>
                                        {[checkpoint.location, checkpoint.unit].filter(Boolean).join(' · ')}
                                    </Text>
                                </div>
                            )}
                            {checkpoint.address && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Address</Text>
                                    <Text style={{ fontSize: 12, textAlign: 'right' }}>
                                        {checkpoint.address.label ??
                                            `${checkpoint.address.streetNumber ?? ''} ${checkpoint.address.streetName ?? ''}, ${checkpoint.address.city ?? ''}`}
                                    </Text>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Scan Radius</Text>
                                <Text style={{ fontSize: 12 }}>
                                    {checkpoint.scanRadiusMeters ? `${checkpoint.scanRadiusMeters} m` : '—'}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Follow-ups</Text>
                                <Text style={{ fontSize: 12 }}>
                                    {checkpoint.allowOpenFollowUp ? 'Allowed' : 'Not allowed'}
                                </Text>
                            </div>
                            {checkpoint.description && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Description</Text>
                                    <Text style={{ fontSize: 12 }}>{checkpoint.description}</Text>
                                </div>
                            )}
                            {checkpoint.instructions && (
                                <div>
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Guard Instructions</Text>
                                    <Text style={{ fontSize: 12 }}>{checkpoint.instructions}</Text>
                                </div>
                            )}
                        </Space>
                    </div>
                )}

                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: '#888' }}>Loading QR data…</div>
                    </div>
                )}

                {!loading && metaError && (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#ef4444' }}>
                        Failed to load QR metadata.{' '}
                        <Button type="link" onClick={refetchMeta}>Retry</Button>
                    </div>
                )}

                {!loading && !metaError && qrMeta && (
                    <>
                        {/* Meta info */}
                        <div
                            style={{
                                background: '#f8faff',
                                borderRadius: 10,
                                padding: '14px 18px',
                                marginBottom: 20,
                                border: '1px solid #e8eaf6',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Version</Text>
                                <Tag color="blue">v{qrMeta.version}</Tag>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Generated</Text>
                                <Text style={{ fontSize: 12 }}>
                                    {qrMeta.generatedAt ? dayjs(qrMeta.generatedAt).format('DD MMM YYYY, HH:mm') : '—'}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Public ID</Text>
                                <Text code style={{ fontSize: 10 }}>{qrMeta.publicId?.slice(0, 18)}…</Text>
                            </div>
                        </div>

                        {qrConflict ? (
                            <Alert
                                type="error"
                                showIcon
                                message="This QR image cannot be regenerated"
                                description={qrConflict}
                                action={
                                    <Button
                                        size="small"
                                        danger
                                        icon={<ReloadOutlined />}
                                        onClick={() => setRotateConfirmVisible(true)}
                                    >
                                        Rotate QR
                                    </Button>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        ) : (
                            <>
                                {/* QR Preview */}
                                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                    {previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt="QR Code Preview"
                                            style={{
                                                width: 200,
                                                height: 200,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8,
                                                objectFit: 'contain',
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: 200,
                                                height: 200,
                                                background: '#f3f4f6',
                                                borderRadius: 8,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#9ca3af',
                                                fontSize: 13,
                                            }}
                                        >
                                            Preview unavailable
                                        </div>
                                    )}
                                </div>

                                <Divider style={{ margin: '12px 0' }} />

                                {/* Download buttons */}
                                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownload('png')}
                                        style={{ flex: 1 }}
                                    >
                                        Download PNG
                                    </Button>
                                    <Button
                                        icon={<FilePdfOutlined />}
                                        onClick={() => handleDownload('pdf')}
                                        style={{ flex: 1 }}
                                    >
                                        Download PDF
                                    </Button>
                                </div>

                                {/* Rotate QR */}
                                <Tooltip title="Rotation immediately revokes all older printed QR labels.">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        danger
                                        block
                                        onClick={() => setRotateConfirmVisible(true)}
                                    >
                                        Rotate QR Code
                                    </Button>
                                </Tooltip>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, padding: '8px 12px', background: '#fff7ed', borderRadius: 8, border: '1px solid #fed7aa' }}>
                                    <InfoCircleOutlined style={{ color: '#ea580c', marginTop: 2 }} />
                                    <Text style={{ fontSize: 11, color: '#c2410c' }}>
                                        After rotation, every previously printed QR for this checkpoint will stop working. You must reprint and replace all labels.
                                    </Text>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal>

            {/* Destructive rotate confirmation */}
            <Modal
                title={
                    <Space>
                        <ReloadOutlined style={{ color: '#ef4444' }} />
                        <span style={{ color: '#ef4444' }}>Rotate QR Code</span>
                    </Space>
                }
                open={rotateConfirmVisible}
                onCancel={() => setRotateConfirmVisible(false)}
                onOk={() => rotateMutation.mutate()}
                okText="Yes, Rotate QR"
                okButtonProps={{ danger: true, loading: rotateMutation.isLoading }}
                cancelText="Cancel"
                width={420}
            >
                <p style={{ marginBottom: 12 }}>
                    Rotating this QR will invalidate all previously printed versions of{' '}
                    <strong>{checkpointName}</strong>. Continue?
                </p>
                <p style={{ color: '#6b7280', fontSize: 13 }}>
                    This action is <strong>irreversible</strong>. Only proceed if you can reprint and physically replace the labels at this checkpoint.
                </p>
            </Modal>
        </>
    );
};

export default QrPanel;
